import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  Table,
  useReactTable,
} from '@tanstack/react-table'
import classNames from 'classnames'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Routes } from '../../routes'
import { useGetRepositories } from '../../utils/api-client/api-hooks'
import { type Repository } from '../../utils/api-client/api-schemas'
import { useIsStalePlaceholderData } from '../../utils/query-utils'
import { PaginationControls } from '../table/pagination-controls'
import { isInteractiveElementClick } from '../table/row-interaction'
import { type Pagination } from '../table/table'
import { TableBodySkeleton } from '../table/table-body-skeleton'
import {
  layoutOfExtraColumns,
  useRepositoriesTableColumnDefinitions,
  type UseRepositoriesTableColumnDefinitionsOptions,
} from './repositories-table.columns'
import * as styles from './repositories-table.css'

type GetRepositoriesQueryResult = Pick<
  ReturnType<typeof useGetRepositories>,
  'data' | 'isLoading' | 'isPlaceholderData'
>

// #region Impure Component (handles data fetching)
type RepositoriesTableProps = Omit<RepositoriesTablePureProps, 'getRepositoriesQueryResult'> & {
  /** Project ids (or `ungrouped`) the page is filtering on; the same key the page's own query uses. */
  projectIds?: string[]
  /** The integration instance the page is filtering on; the same key the page's own query uses. */
  integrationId?: string
}

export const RepositoriesTable: React.FC<RepositoriesTableProps> = ({
  pagination,
  searchTerm,
  projectIds,
  integrationId,
  ...rest
}) => {
  const getRepositoriesQueryResult = useGetRepositories({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    searchTerm,
    projectIds,
    integrationId,
    // This table has a project column, so it is one of the few lists that needs the membership inline.
    embedProjectMembership: true,
  })

  return (
    <RepositoriesTablePure
      getRepositoriesQueryResult={getRepositoriesQueryResult}
      pagination={pagination}
      searchTerm={searchTerm}
      {...rest}
    />
  )
}
// #endregion Impure Component

// #region Pure Component (for testing/stories)
type RepositoriesTablePureProps = RepositoriesReactTableProps & RepositoriesTablePaginationControlsProps

export const RepositoriesTablePure: React.FC<RepositoriesTablePureProps> = ({
  columnDefinitionOptions,
  getRepositoriesQueryResult,
  pagination,
  onPaginationChange,
  onOpenAddRepository,
  searchTerm,
  filteredEmptyState,
  extraColumns,
  rows,
}) => {
  return (
    <>
      <RepositoriesReactTable
        columnDefinitionOptions={columnDefinitionOptions}
        getRepositoriesQueryResult={getRepositoriesQueryResult}
        searchTerm={searchTerm}
        filteredEmptyState={filteredEmptyState}
        onOpenAddRepository={onOpenAddRepository}
        extraColumns={extraColumns}
        rows={rows}
      />

      {!getRepositoriesQueryResult.isLoading && (
        <RepositoriesTablePaginationControls
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          getRepositoriesQueryResult={getRepositoriesQueryResult}
        />
      )}
    </>
  )
}
// #endregion Pure Component

// The header and action bar now lives with the page, in `repositories-controls.tsx`: its search field,
// connect action and view switch have to persist across the table and the risk views alike.

// #region Repositories React Table
type RepositoriesReactTableProps = Omit<RepositoriesTableHeaderProps & RepositoriesTableBodyProps, 'table'> & {
  /** The layout is not among them: the table reads it from `extraColumns`. */
  columnDefinitionOptions: Omit<UseRepositoriesTableColumnDefinitionsOptions, 'layout'>
  /**
   * Extra columns, spliced in before Actions so that stays rightmost.
   *
   * Supplied by the page rather than built here: the risk columns need types that live with the route,
   * and `architecture.test.ts` forbids a component importing from `routes/`.
   */
  extraColumns?: ColumnDef<Repository>[]
  /**
   * The rows to render, already ordered and filtered. Omitted, the fetched page stands as the API sent it.
   *
   * A list rather than the comparator and predicate this used to take. Those made the table apply a
   * filter the page had *also* applied to count what was visible — two copies of one derivation, free to
   * drift, and the header and the rows disagreeing is exactly how that drift would show. The page owns
   * this now because the ordering comes from risk data the table cannot see.
   */
  rows?: Repository[]
}

const RepositoriesReactTable: React.FC<RepositoriesReactTableProps> = ({
  columnDefinitionOptions,
  getRepositoriesQueryResult,
  searchTerm,
  filteredEmptyState,
  onOpenAddRepository,
  extraColumns,
  rows: providedRows,
}) => {
  const baseColumns = useRepositoriesTableColumnDefinitions({
    ...columnDefinitionOptions,
    layout: layoutOfExtraColumns(extraColumns),
  })
  const columns = useMemo(() => {
    if (!extraColumns?.length) {
      return baseColumns
    }
    // Anchored on the scan column so extra columns sit directly after the repository they describe,
    // ahead of the scan and Actions. Falling back to before Actions, then to the end, keeps the splice
    // sane if either anchor is ever dropped.
    const anchors = ['analysis', 'actions']
    const anchorIndex = anchors.map(id => baseColumns.findIndex(column => column.id === id)).find(index => index !== -1)
    const insertAt = anchorIndex ?? baseColumns.length
    return [...baseColumns.slice(0, insertAt), ...extraColumns, ...baseColumns.slice(insertAt)]
  }, [baseColumns, extraColumns])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // Not TanStack's sorting model: the order comes from data the table cannot see, so the page does the
  // ordering and filtering and hands down the result.
  const rows = useMemo(
    () => providedRows ?? getRepositoriesQueryResult.data?._embedded.items ?? [],
    [providedRows, getRepositoriesQueryResult.data]
  )

  const table = useReactTable({
    getRowId: data => data.id,
    data: rows,
    columns,
    enableRowSelection: true,
    enableGlobalFilter: false,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  })

  return (
    <table className={styles.table}>
      {/* Widths declared by extra columns are applied here, the way `table.tsx` does it — the table's
          own cell rules were written for its three columns and cannot size the ones spliced in. */}
      <colgroup>
        {table.getAllLeafColumns().map(column => (
          <col key={column.id} style={{ width: column.columnDef.meta?.width }} />
        ))}
      </colgroup>
      <RepositoriesTableHeader table={table} />
      <RepositoriesTableBody
        table={table}
        getRepositoriesQueryResult={getRepositoriesQueryResult}
        searchTerm={searchTerm}
        filteredEmptyState={filteredEmptyState}
        onOpenAddRepository={onOpenAddRepository}
      />
    </table>
  )
}

type RepositoriesTableHeaderProps = {
  table: Table<Repository>
}

const RepositoriesTableHeader: React.FC<RepositoriesTableHeaderProps> = ({ table }) => {
  return (
    <thead className={styles.tableHead}>
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            return (
              <th
                key={header.id}
                className={classNames(
                  styles.columnHeader,
                  styles.cell,
                  styles.installationsTableCell,
                  header.column.columnDef.meta?.className
                )}
              >
                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}

/** Why an empty page is empty when a filter is active, so the empty state can say so instead of offering to connect. */
export type FilteredEmptyState = 'filters' | 'integration' | 'project' | 'projects' | 'ungrouped'

type RepositoriesTableBodyProps = {
  table: Table<Repository>
  getRepositoriesQueryResult: GetRepositoriesQueryResult
  searchTerm: string
  filteredEmptyState?: FilteredEmptyState
  onOpenAddRepository: () => unknown
}

const RepositoriesTableBody: React.FC<RepositoriesTableBodyProps> = ({
  table,
  getRepositoriesQueryResult: { isPlaceholderData, isLoading },
  searchTerm,
  filteredEmptyState,
  onOpenAddRepository,
}) => {
  const isStalePlaceholderData = useIsStalePlaceholderData(isPlaceholderData)
  const navigate = useNavigate()

  if (isLoading || isStalePlaceholderData) {
    return <TableBodySkeleton rowCount={7} columnCount={table.getAllLeafColumns().length} rowHeightInPixels={43} />
  }

  const navigateToRepository = (repositoryId: string, event: React.SyntheticEvent) => {
    // Interactive cell content (name link, action buttons, upload link) handles its own click/navigation.
    if (isInteractiveElementClick(event)) {
      return
    }
    navigate(Routes.RepositoryDetailsPage.createPath({ repositoryId }))
  }

  return (
    <tbody>
      {table.getRowModel().rows?.length ? (
        table.getRowModel().rows.map(row => (
          <tr
            key={row.id}
            className={classNames(styles.row, styles.selectableRow)}
            tabIndex={0}
            onClick={event => navigateToRepository(row.original.id, event)}
            onKeyDown={event => {
              // Only intercept Enter/Space for rows; let interactive descendants keep
              // their native key handling (checked before preventDefault).
              if (event.key !== 'Enter' && event.key !== ' ') {
                return
              }
              if (isInteractiveElementClick(event)) {
                return
              }
              event.preventDefault()
              navigateToRepository(row.original.id, event)
            }}
          >
            {row.getVisibleCells().map(cell => (
              <td
                key={cell.id}
                className={classNames(
                  styles.cell,
                  styles.installationsTableCell,
                  cell.column.columnDef.meta?.className
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))
      ) : (
        <tr>
          <td className={styles.emptyStateCell} colSpan={table.getAllLeafColumns().length}>
            <div className={styles.emptyStateContainer}>
              {searchTerm ? (
                <div className={styles.emptyStateText}>No repositories match your search</div>
              ) : filteredEmptyState === 'filters' ? (
                <div className={styles.emptyStateText}>No repositories match these filters</div>
              ) : filteredEmptyState === 'integration' ? (
                <div className={styles.emptyStateText}>No repositories use this integration</div>
              ) : filteredEmptyState === 'ungrouped' ? (
                <>
                  <div className={styles.emptyStateText}>No ungrouped repositories</div>
                  <div className={styles.emptyStateSubtext}>Every repository belongs to a project.</div>
                </>
              ) : filteredEmptyState === 'project' ? (
                <>
                  <div className={styles.emptyStateText}>No repositories in this project</div>
                  <div className={styles.emptyStateSubtext}>Nothing has been assigned here yet.</div>
                </>
              ) : filteredEmptyState === 'projects' ? (
                <>
                  <div className={styles.emptyStateText}>No repositories match this filter</div>
                  <div className={styles.emptyStateSubtext}>Try widening or clearing the project filter.</div>
                </>
              ) : (
                <>
                  <div className={styles.emptyStateText}>No repositories</div>
                  <div className={styles.emptyStateSubtext}>
                    <button type="button" className={styles.emptyStateLink} onClick={onOpenAddRepository}>
                      Connect
                    </button>{' '}
                    using your preferred method
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </tbody>
  )
}
// #endregion Repositories React Table

// #region Repositories Table Pagination Controls
type RepositoriesTablePaginationControlsProps = {
  pagination: Pagination
  onPaginationChange: (pagination: Pagination) => unknown
  getRepositoriesQueryResult: GetRepositoriesQueryResult
}

const RepositoriesTablePaginationControls: React.FC<RepositoriesTablePaginationControlsProps> = ({
  pagination,
  onPaginationChange,
  getRepositoriesQueryResult,
}) => {
  return (
    <div className={styles.tableFooter}>
      <PaginationControls
        tableName="Installations"
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        totalItems={getRepositoriesQueryResult.data?.total ?? 0}
        onPageIndexChange={pageIndex => onPaginationChange({ pageIndex, pageSize: pagination.pageSize })}
        onPageSizeChange={pageSize => onPaginationChange({ pageIndex: pagination.pageIndex, pageSize })}
      />
    </div>
  )
}
// #endregion Repositories Table Pagination Controls
